import React from 'react';
import { Drawer, Form, Button, Message, useToaster, TagPicker, InputNumber, Toggle } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { addWfhProfile } from '@/services/wfhProfile.service';
import { useTranslation } from 'react-i18next';

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
}

const dayOptions = [
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
  { label: 'Sunday', value: 'Sunday' }
];
const DrawerView: React.FC<DrawerViewProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const wfhProfiles = useSelector(state => state.wfhProfile.data);

  const [formValue, setFormValue] = React.useState<any>({
    name: ''
  });

  React.useEffect(() => {
    if (open) {
      setFormValue({
        name: '',
        authorizedWfhDays: [],
        daysPerWeekWithBalance: 0,
        daysPerWeekWithoutBalance: 0,
        autoAcceptIfAuthorizedDays: false,
        allowSubmitForUnauthorizedDays: false
      });
    }
  }, [open]);

  const isDuplicateGrade = (name: string) => {
    return wfhProfiles.some(wfhProfile => wfhProfile.name.toLowerCase() === name.toLowerCase());
  };

  const handleSubmit = async () => {
    const trimmedGradeName = formValue.name.trim();

    if (!trimmedGradeName) return;

    if (isDuplicateGrade(trimmedGradeName)) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          Home office profile "{trimmedGradeName}" already exists
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }

    try {
      await dispatch(
        addWfhProfile({
          name: trimmedGradeName,
          authorizedWfhDays: formValue.authorizedWfhDays,
          daysPerWeekWithBalance: formValue.daysPerWeekWithBalance,
          daysPerWeekWithoutBalance: formValue.daysPerWeekWithoutBalance,
          autoAcceptIfAuthorizedDays: formValue.autoAcceptIfAuthorizedDays,
          allowSubmitForUnauthorizedDays: formValue.allowSubmitForUnauthorizedDays
        })
      );
      toaster.push(
        <Message type="success" duration={3000} showIcon>
          {t('Home office profile successfully created')}
        </Message>,
        { placement: 'topCenter' }
      );
      onClose();
    } catch (error) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {t('Failed to create grade. Please try again.')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
  };
  const { t } = useTranslation();

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add Home Office Profile')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          {/* Name Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Name')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Enter profile name" />
          </Form.Group>

          {/* Authorized WFH Days Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Authorized Home Office Days')}</Form.ControlLabel>
            <TagPicker
              name="authorizedWfhDays"
              data={dayOptions}
              value={formValue.authorizedWfhDays}
              onChange={value => setFormValue(prev => ({ ...prev, authorizedWfhDays: value }))}
              placeholder={t('Select authorized home office days')}
              block
              searchable
            />
          </Form.Group>

          {/* Days Per Week With Balance Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Days Per Week With Balance')}</Form.ControlLabel>
            <InputNumber
              name="daysPerWeekWithBalance"
              min={0}
              value={formValue.daysPerWeekWithBalance}
              onChange={value =>
                setFormValue(prev => ({
                  ...prev,
                  daysPerWeekWithBalance: value,
                  allowSubmitForUnauthorizedDays: value > 0 ? true : false
                }))
              }
              placeholder={t('Enter days per week with balance')}
            />
          </Form.Group>

          {/* Days Per Week Without Balance Field */}
          <Form.Group>
            <Form.ControlLabel>{t('Days Per Week Without Balance')}</Form.ControlLabel>
            <InputNumber
              name="daysPerWeekWithoutBalance"
              min={0}
              value={formValue.daysPerWeekWithoutBalance}
              onChange={value =>
                setFormValue(prev => ({ ...prev, daysPerWeekWithoutBalance: value }))
              }
              placeholder={t('Enter days per week without balance')}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>
              {t('Auto-accept fully authorized Home Office requests')}{' '}
              <Form.HelpText tooltip>
                {t(
                  'When this option is enabled, a home office request is automatically accepted if all requested days match the authorized weekdays. If one or more days are not authorized, the request status will be set to pending and requires manual approval.'
                )}
              </Form.HelpText>
            </Form.ControlLabel>

            <Toggle
              onChange={(checked: boolean, event) =>
                setFormValue(prev => ({ ...prev, autoAcceptIfAuthorizedDays: checked }))
              }
              checked={formValue.autoAcceptIfAuthorizedDays}
            />
          </Form.Group>
          {(formValue.daysPerWeekWithoutBalance > formValue.authorizedWfhDays?.length ||
            formValue.daysPerWeekWithBalance > 0) && (
            <Form.Group>
              <Form.ControlLabel>
                {t('Allow Home Office requests on unauthorized days')}
                <Form.HelpText tooltip>
                  {t(
                    'If this option is enabled, users are allowed to submit home office requests even if some days are not part of the authorized WFH days defined in their profile. These requests will be either marked as pending or will consume balance if available, and as long as the weekly WFH limit is not exceeded. If this option is disabled, any submission that includes an unauthorized day will be automatically rejected, even if the weekly limit has not been reached'
                  )}
                </Form.HelpText>
              </Form.ControlLabel>
              <Toggle
                onChange={(checked: boolean, event) =>
                  setFormValue(prev => ({ ...prev, allowSubmitForUnauthorizedDays: checked }))
                }
                checked={formValue.allowSubmitForUnauthorizedDays}
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
